// AT Deposit Portal — user submits an INR payment (UPI / QR / Bank) + txn ref → admin approves → AT credited.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../utils/api';
import ATWalletBadge from '../../components/ATWalletBadge';
import { fmtDateTime } from '../../utils/dateFmt';

export default function ATDepositPage() {
  const [settings, setSettings] = useState(null);
  const [mine, setMine] = useState([]);
  const [method, setMethod] = useState('upi');
  const [amount, setAmount] = useState('');
  const [txnRef, setTxnRef] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payerAcct, setPayerAcct] = useState('');
  const [notes, setNotes] = useState('');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const uploadScreenshot = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Max 5MB');
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/at-deposit/screenshot', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setScreenshotUrl(r.data.url);
      toast.success('✓ Screenshot uploaded — will speed up approval');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const load = async () => {
    try {
      const [s, m] = await Promise.all([
        api.get('/at-deposit/settings'),
        api.get('/at-deposit/my', { params: { limit: 20 } }),
      ]);
      setSettings(s.data.settings);
      setMine(m.data.deposits || []);
    } catch (e) { toast.error('Load failed — try again'); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!amount || Number(amount) <= 0) return toast.error('Amount daalein');
    if (!txnRef || txnRef.trim().length < 4) return toast.error('Txn Reference (UTR/UPI Ref) daalein');
    setBusy(true);
    try {
      await api.post('/at-deposit/create', {
        amount: Number(amount),
        method,
        txn_ref: txnRef.trim(),
        payer_name: payerName || null,
        payer_upi_or_account: payerAcct || null,
        screenshot_url: screenshotUrl || null,
        notes: notes || null,
      });
      toast.success('✓ Deposit request submitted — admin will approve within 30 min');
      setAmount(''); setTxnRef(''); setPayerName(''); setPayerAcct(''); setNotes(''); setScreenshotUrl('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
    finally { setBusy(false); }
  };

  const copy = (val) => { navigator.clipboard.writeText(val); toast.success('Copied'); };

  if (!settings) return <div className="min-h-screen pt-24 text-white text-center">⏳ Loading deposit portal…</div>;

  const s = settings;
  const rate = s.rate || 1.0;
  const atPreview = amount ? (Number(amount) * rate).toFixed(2) : '0.00';
  const qrUrl = s.qr_image_url?.startsWith('http') ? s.qr_image_url : `${process.env.REACT_APP_BACKEND_URL}${s.qr_image_url}`;
  const upiIntent = s.upi_id && amount ? `upi://pay?pa=${encodeURIComponent(s.upi_id)}&pn=${encodeURIComponent(s.upi_payee_name || 'Anant')}&am=${amount}&cu=INR` : null;

  return (
    <div className="min-h-screen pt-20 pb-16 px-3 bg-gradient-to-br from-slate-950 via-cyan-950/40 to-slate-950 text-white" data-testid="at-deposit-page">
      <ATWalletBadge />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <Link to="/wallets" className="text-xs text-slate-400">← Wallets</Link>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black">➕ AT Deposit Portal</h1>
        <p className="text-[11px] opacity-70 mb-3">Pay ₹ via UPI / QR / Bank · Submit txn reference · Admin credits AT within 30 min · Rate: <b className="text-cyan-300">1 INR = {rate} AT</b></p>

        {!s.is_active && (
          <div className="rounded-xl border-2 border-red-400 bg-red-500/20 p-3 mb-3 text-sm font-black text-red-100">⚠️ AT Deposits currently disabled by admin</div>
        )}

        {/* Payment method tabs */}
        <div className="grid grid-cols-3 gap-1 p-1 rounded-2xl border border-slate-700 bg-slate-900/70 mb-3">
          {[['upi','🟣 UPI'],['qr','📱 QR'],['bank','🏦 Bank']].map(([id,l]) => (
            <button key={id} onClick={() => setMethod(id)} data-testid={`atdep-method-${id}`} className={`px-2 py-1.5 rounded text-xs font-black ${method === id ? 'bg-cyan-500 text-black' : 'text-slate-300 hover:bg-slate-800'}`}>{l}</button>
          ))}
        </div>

        {/* Payment target block */}
        <div className="rounded-2xl border-2 border-cyan-400/40 bg-slate-900/70 p-3 mb-3">
          {method === 'upi' && (
            <div className="space-y-2" data-testid="atdep-upi-block">
              <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">🟣 Pay via UPI</div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="text-[10px] opacity-70">UPI ID</div>
                  <div className="text-base font-black text-cyan-200" data-testid="atdep-upi-id">{s.upi_id}</div>
                  <div className="text-[10px] opacity-70">Payee: {s.upi_payee_name}</div>
                </div>
                <button onClick={() => copy(s.upi_id)} className="px-2 py-1 rounded bg-slate-700 text-white text-xs font-black">Copy</button>
                {upiIntent && <a href={upiIntent} className="px-2 py-1 rounded bg-green-500 text-black text-xs font-black">🚀 Open UPI App</a>}
              </div>
            </div>
          )}
          {method === 'qr' && (
            <div className="text-center" data-testid="atdep-qr-block">
              <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-2">📱 Scan our Bank QR</div>
              <img src={qrUrl} alt="Deposit QR" className="mx-auto rounded-lg border-2 border-cyan-400/50 bg-white p-2" style={{ maxWidth: 260 }} onError={(e) => { e.target.style.display = 'none'; }} />
              <div className="text-[10px] opacity-70 mt-2">UPI ID: <b className="text-cyan-200">{s.upi_id}</b></div>
              <div className="text-[10px] opacity-70">Payee: {s.upi_payee_name}</div>
            </div>
          )}
          {method === 'bank' && (
            <div className="space-y-1 text-xs" data-testid="atdep-bank-block">
              <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-1">🏦 Bank Transfer (NEFT/IMPS)</div>
              {[['Account Name', s.bank_account_name], ['Account Number', s.bank_account_number], ['IFSC', s.bank_ifsc], ['Bank', s.bank_name]].map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 rounded bg-slate-800/60 p-2">
                  <div className="flex-1"><div className="text-[9px] opacity-70">{k}</div><div className="font-black text-cyan-200">{v}</div></div>
                  <button onClick={() => copy(v)} className="px-2 py-0.5 rounded bg-slate-700 text-white text-[10px] font-black">Copy</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-3 mb-3 text-[11px] whitespace-pre-line text-amber-100">
          <div className="font-black text-amber-200 mb-1">📖 Steps</div>
          {s.instructions}
        </div>

        {/* Submission form */}
        <div className="rounded-2xl border-2 border-emerald-400/40 bg-emerald-500/5 p-3 space-y-2" data-testid="atdep-form">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">Submit Payment Reference</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col text-[10px] opacity-90"><span className="mb-0.5">Amount (₹)</span>
              <input type="number" step="0.01" min={s.min_amount} max={s.max_amount} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="atdep-amount" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </label>
            <label className="flex flex-col text-[10px] opacity-90"><span className="mb-0.5">AT to credit</span>
              <div className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs text-cyan-300 font-black">{atPreview} AT</div>
            </label>
            <label className="flex flex-col text-[10px] opacity-90 col-span-2"><span className="mb-0.5">Txn Reference (UTR / UPI Ref / Bank Ref)*</span>
              <input value={txnRef} onChange={(e) => setTxnRef(e.target.value)} data-testid="atdep-txnref" placeholder="e.g. 4123XXXXXX789" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </label>
            <label className="flex flex-col text-[10px] opacity-90"><span className="mb-0.5">Payer Name (optional)</span>
              <input value={payerName} onChange={(e) => setPayerName(e.target.value)} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </label>
            <label className="flex flex-col text-[10px] opacity-90"><span className="mb-0.5">Payer UPI / Account (optional)</span>
              <input value={payerAcct} onChange={(e) => setPayerAcct(e.target.value)} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </label>
            <label className="flex flex-col text-[10px] opacity-90 col-span-2"><span className="mb-0.5">Notes (optional)</span>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </label>
            <label className="flex flex-col text-[10px] opacity-90 col-span-2">
              <span className="mb-0.5">📸 Payment Screenshot <span className="opacity-70">(optional but speeds up approval)</span></span>
              <input type="file" accept="image/*" onChange={(e) => uploadScreenshot(e.target.files?.[0])} data-testid="atdep-screenshot" className="text-[11px] file:mr-2 file:px-2 file:py-1 file:rounded file:border-0 file:bg-cyan-500 file:text-black file:font-black" />
              {uploading && <span className="text-[10px] text-cyan-300 mt-1">⏳ Uploading…</span>}
              {screenshotUrl && !uploading && (
                <div className="mt-1 flex items-center gap-2">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${screenshotUrl}`} alt="Preview" className="w-16 h-16 rounded border border-cyan-400/40 object-cover" />
                  <button onClick={() => setScreenshotUrl('')} className="text-[10px] px-2 py-0.5 rounded bg-red-500/25 text-red-300">✕ Remove</button>
                </div>
              )}
            </label>
          </div>
          <button onClick={submit} disabled={busy || !s.is_active} data-testid="atdep-submit" className="w-full px-3 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-black text-sm font-black disabled:opacity-40">
            {busy ? '⏳ Submitting…' : '✓ Submit Deposit Request'}
          </button>
        </div>

        {/* History */}
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-300 font-black mb-2">Your Recent Deposits</div>
          {mine.length === 0 && <div className="text-xs opacity-60 text-center py-6">No deposits yet</div>}
          <div className="space-y-1">
            {mine.map((d) => (
              <div key={d.id} className="flex items-center gap-2 rounded-xl bg-slate-900/60 border border-slate-700 p-2 text-xs" data-testid={`atdep-row-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-black truncate">₹{d.amount_inr} · {d.at_equivalent} AT · {d.method?.toUpperCase()} <span className="opacity-60">· ref {d.txn_ref}</span></div>
                  <div className="text-[9px] opacity-60">{fmtDateTime(d.created_at)}{d.admin_notes ? ` · admin: ${d.admin_notes}` : ''}</div>
                </div>
                <div className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-black ${d.status === 'approved' ? 'bg-emerald-500 text-black' : d.status === 'rejected' ? 'bg-red-500 text-white' : 'bg-amber-500 text-black'}`}>{d.status}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
